import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import {
  stats as statsRaw,
  technologyStack,
  technologyGroups,
  services,
  industries,
  caseStudies,
  testimonials,
  insights,
  process,
  principles,
  clientEnvironments,
} from '../data/site';

const stats = Array.isArray(statsRaw) ? statsRaw : (statsRaw.global || []);

/* ─── Animated Counter ─── */
function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const num = parseInt(target.replace(/[^0-9]/g, ''), 10) || 0;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let start = 0;
        const step = Math.max(1, Math.ceil(num / 50));
        const timer = setInterval(() => {
          start += step;
          if (start >= num) { setCount(num); clearInterval(timer); }
          else setCount(start);
        }, 30);
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, [target]);
  return <span ref={ref}>{count}{suffix}</span>;
}

/* ─── Scroll Reveal Hook ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('visible'); obs.disconnect(); }
    }, { threshold: 0.12 });
    if (el) obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

/* ─── Premium Hero Visual — AI Analytics Platform ─── */
function HeroVisual() {
  const heroBadges = technologyStack.slice(0, 6);
  const flow = ['Sources', 'Lakehouse', 'Models', 'Apps'];

  return (
    <div className="hero-visual-wrap" aria-hidden="true">
      <div className="hero-visual-glow" />
      <div className="hero-tech-orbit">
        {heroBadges.map((tech, index) => (
          <span className={`hero-tech-badge hero-tech-badge-${index + 1} tone-${tech.tone}`} key={tech.name}>
            <span>{tech.short}</span>
            <strong>{tech.category}</strong>
          </span>
        ))}
      </div>
      <div className="hero-dashboard">
        {/* Browser chrome */}
        <div className="dash-header">
          <div className="dash-dots"><span/><span/><span/></div>
          <div className="dash-url-bar">
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none"><path d="M8 1a5 5 0 00-5 5v2a5 5 0 0010 0V6a5 5 0 00-5-5z" stroke="#64748b" strokeWidth="1.5"/><rect x="5" y="8" width="6" height="5" rx="1" fill="#64748b"/></svg>
            <span>app.dalytix.ai/dashboard</span>
          </div>
          <span className="dash-status">● Live</span>
        </div>

        {/* Top nav bar */}
        <div className="dash-nav">
          <div className="dash-nav-brand">
            <img src="/images/dalytix-logo-pro.svg" alt="" className="dash-nav-logo"/>
            <span>Dalytix</span>
          </div>
          <div className="dash-nav-tabs">
            <span className="dash-tab active">Overview</span>
            <span className="dash-tab">Models</span>
            <span className="dash-tab">Pipelines</span>
            <span className="dash-tab">Data</span>
          </div>
          <div className="dash-nav-user">
            <div className="dash-avatar"/>
          </div>
        </div>

        {/* Dashboard body */}
        <div className="dash-body">
          {/* KPI row */}
          <div className="dash-kpi-row">
            <div className="dash-kpi">
              <span className="dash-kpi-label">Data Quality</span>
              <span className="dash-kpi-value">99.2%</span>
              <span className="dash-kpi-change up">+4.8%</span>
            </div>
            <div className="dash-kpi">
              <span className="dash-kpi-label">Model Drift</span>
              <span className="dash-kpi-value">Low</span>
              <span className="dash-kpi-change up">Guarded</span>
            </div>
            <div className="dash-kpi">
              <span className="dash-kpi-label">Latency</span>
              <span className="dash-kpi-value">82ms</span>
              <span className="dash-kpi-change up">p95</span>
            </div>
            <div className="dash-kpi">
              <span className="dash-kpi-label">Cost Guardrail</span>
              <span className="dash-kpi-value">On</span>
              <span className="dash-kpi-change neutral">Daily</span>
            </div>
          </div>

          <div className="dash-flow">
            {flow.map((item, index) => (
              <React.Fragment key={item}>
                <div className="dash-flow-node">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{item}</strong>
                </div>
                {index < flow.length - 1 && <span className="dash-flow-link" />}
              </React.Fragment>
            ))}
          </div>

          {/* Main chart + sidebar */}
          <div className="dash-main-grid">
            <div className="dash-chart-panel">
              <div className="dash-panel-head">
                <span className="dash-panel-title">Governed Model Performance</span>
                <div className="dash-legend">
                  <span className="dash-legend-item"><span className="dash-dot indigo"/>Quality</span>
                  <span className="dash-legend-item"><span className="dash-dot slate"/>Baseline</span>
                </div>
              </div>
              <svg viewBox="0 0 400 120" fill="none" className="dash-chart-svg">
                {/* Grid lines */}
                <line x1="0" y1="30" x2="400" y2="30" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5"/>
                <line x1="0" y1="60" x2="400" y2="60" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5"/>
                <line x1="0" y1="90" x2="400" y2="90" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5"/>
                {/* Area fills */}
                <path d="M0,90 C30,85 60,70 100,60 C140,50 180,45 220,35 C260,25 300,20 340,18 C370,16 390,15 400,14 L400,120 L0,120 Z" fill="rgba(79,70,229,0.12)"/>
                <path d="M0,95 C30,88 60,75 100,65 C140,55 180,48 220,40 C260,32 300,28 340,22 C370,19 390,17 400,16 L400,120 L0,120 Z" fill="rgba(148,163,184,0.08)"/>
                {/* Line - Actual */}
                <path d="M0,90 C30,85 60,70 100,60 C140,50 180,45 220,35 C260,25 300,20 340,18 C370,16 390,15 400,14" stroke="#4f46e5" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                {/* Line - Predicted */}
                <path d="M0,95 C30,88 60,75 100,65 C140,55 180,48 220,40 C260,32 300,28 340,22 C370,19 390,17 400,16" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="6 3" />
                {/* Data points */}
                <circle cx="100" cy="60" r="3" fill="#4f46e5" opacity="0.9"/>
                <circle cx="220" cy="35" r="3" fill="#4f46e5" opacity="0.9"/>
                <circle cx="340" cy="18" r="3" fill="#4f46e5" opacity="0.9"/>
              </svg>
            </div>

            {/* Side panel - Model status */}
            <div className="dash-side-panel">
              <span className="dash-panel-title">Production Controls</span>
              <div className="dash-model-list">
                <div className="dash-model-item">
                  <div className="dash-model-info">
                    <span className="dash-model-name">PII Policy</span>
                    <span className="dash-model-meta">masked · audit-ready</span>
                  </div>
                  <span className="dash-model-badge indigo">Active</span>
                </div>
                <div className="dash-model-item">
                  <div className="dash-model-info">
                    <span className="dash-model-name">Eval Harness</span>
                    <span className="dash-model-meta">runs on release</span>
                  </div>
                  <span className="dash-model-badge indigo">Pass</span>
                </div>
                <div className="dash-model-item">
                  <div className="dash-model-info">
                    <span className="dash-model-name">Cost Guardrail</span>
                    <span className="dash-model-meta">budget threshold</span>
                  </div>
                  <span className="dash-model-badge slate">Watch</span>
                </div>
                <div className="dash-model-item">
                  <div className="dash-model-info">
                    <span className="dash-model-name">Lineage Graph</span>
                    <span className="dash-model-meta">source to app</span>
                  </div>
                  <span className="dash-model-badge indigo">Live</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom status bar */}
          <div className="dash-footer">
            <div className="dash-footer-item">
              <span className="dash-pulse indigo"/>
              <span>Governance Checks Passing</span>
            </div>
            <div className="dash-footer-item">
              <span>Last eval: 12m ago</span>
            </div>
            <div className="dash-footer-item">
              <span>Owner: Platform Team</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Home = () => {
  const revServices = useReveal();
  const revTech = useReveal();
  const revStats = useReveal();
  const revCases = useReveal();
  const revProcess = useReveal();
  const revIndustries = useReveal();
  const revTestimonials = useReveal();
  const revPrinciples = useReveal();
  const revInsights = useReveal();
  const revClients = useReveal();
  const revCta = useReveal();

  return (
    <div className="home-page page-shell">
      {/* ═══ HERO ═══ */}
      <section className="hero">
        <div className="site-container-wide hero-grid">
          <div className="hero-content">
            <span className="section-eyebrow">Enterprise AI & Data Consulting</span>
            <h1>
              We build AI systems that <span className="gradient-text serif-accent">actually ship.</span>
            </h1>
            <p className="hero-sub">
              From strategy to production — Dalytix delivers enterprise AI, data platforms,
              and MLOps that drive measurable business outcomes.
            </p>
            <div className="hero-actions">
              <Link to="/request-demo" className="btn-primary-large">Book a Consultation</Link>
              <Link to="/case-studies" className="btn-ghost btn-arrow">View Case Studies</Link>
            </div>
            <div className="hero-proof">
              {stats.slice(0, 4).map((s, i) => (
                <React.Fragment key={s.label}>
                  {i > 0 && <span className="hero-proof-divider" />}
                  <div className="hero-proof-item">
                    <strong><AnimatedCounter target={s.value} suffix={s.value.replace(/[0-9]/g, '')} /></strong>
                    <span>{s.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          <HeroVisual />
        </div>
      </section>

      {/* ═══ PARTNER STRIP ═══ */}
      <section className="partner-strip">
        <div className="site-container-wide">
          <span className="partner-label">Platform experience across the modern AI and data stack</span>
          <div className="platform-logo-row">
            {technologyStack.slice(0, 10).map((tech) => (
              <div className={`platform-logo-card tone-${tech.tone}`} key={tech.name}>
                <span className="platform-logo-mark">{tech.short}</span>
                <span className="platform-logo-name">{tech.name}</span>
                <small>{tech.category}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SERVICES ═══ */}
      <section className="section reveal" ref={revServices}>
        <div className="site-container-wide">
          <div className="section-header-center">
            <span className="section-eyebrow">Services</span>
            <h2 className="section-title">Nine practices, one delivery machine.</h2>
            <p className="section-lead">
              From board-level strategy to production infrastructure — every practice works as one team.
            </p>
          </div>
          <div className="services-grid-home">
            {services.slice(0, 9).map((s) => (
              <Link to={`/services/${s.id}`} className="service-card-home professional-card" key={s.id}>
                <div className="service-icon-home">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.short}</p>
              </Link>
            ))}
          </div>
          <div className="section-footer">
            <Link to="/services" className="btn-outline btn-arrow">All services</Link>
          </div>
        </div>
      </section>

      {/* ═══ TECHNOLOGY STACK ═══ */}
      <section className="section tech-stack-section reveal" ref={revTech}>
        <div className="site-container-wide">
          <div className="section-header-center">
            <span className="section-eyebrow">Technology Stack</span>
            <h2 className="section-title">Built around the platforms your teams already trust.</h2>
            <p className="section-lead">
              We design vendor-neutral AI and data systems across cloud, lakehouse, warehouse,
              streaming, MLOps, and GenAI ecosystems.
            </p>
          </div>

          <div className="tech-stack-layout">
            <div className="tech-architecture-card">
              <span className="tech-architecture-label">Reference Architecture</span>
              <div className="tech-architecture-flow">
                <div><strong>01</strong><span>Cloud</span></div>
                <i />
                <div><strong>02</strong><span>Data</span></div>
                <i />
                <div><strong>03</strong><span>AI</span></div>
                <i />
                <div><strong>04</strong><span>Apps</span></div>
              </div>
              <p>Secure foundations, governed data products, evaluated models, and business workflows connected through one operating model.</p>
            </div>

            <div className="tech-groups-grid">
              {technologyGroups.map((group) => (
                <article className="tech-group-card professional-card" key={group.category}>
                  <span className="eyebrow-tag">{group.category}</span>
                  <h3>{group.title}</h3>
                  <p>{group.text}</p>
                  <div className="tech-group-platforms">
                    {group.platforms.map((platform) => {
                      const tech = technologyStack.find((item) => item.name === platform);
                      return <span className={`mini-platform tone-${tech?.tone || 'indigo'}`} key={platform}>{tech?.short || platform}</span>;
                    })}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ STATS ═══ */}
      <section className="section section-dark reveal" ref={revStats}>
        <div className="site-container-wide">
          <span className="section-eyebrow">By the numbers</span>
          <h2 className="section-title">Outcomes, not outputs.</h2>
          <div className="stats-bento">
            {stats.map((s, i) => (
              <div className={`bento-cell bento-cell-${i}`} key={s.label}>
                <strong><AnimatedCounter target={s.value} suffix={s.value.replace(/[0-9]/g, '')} /></strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CASE STUDIES ═══ */}
      <section className="section reveal" ref={revCases}>
        <div className="site-container-wide">
          <div className="section-head-row cases-home-head">
            <div>
              <span className="section-eyebrow">Case Studies</span>
              <h2 className="section-title">Real systems. Real outcomes.</h2>
            </div>
            <p className="section-lead">
              A featured delivery story, followed by compact examples from regulated and operational environments.
            </p>
          </div>
          <div className="cases-grid-home">
            {caseStudies.slice(0, 4).map((c, index) => (
              <Link
                to={`/case-studies/${c.id}`}
                className={`case-card-home professional-card ${index === 0 ? 'case-card-featured' : 'case-card-compact'}`}
                key={c.id}
              >
                <div className="case-card-img">
                  <img src={c.image} alt={c.title} />
                  <span className="eyebrow-tag">{c.industry}</span>
                </div>
                <div className="case-card-content">
                  <h3>{c.title}</h3>
                  <p>{c.summary}</p>
                  <div className="case-card-metric">
                    <strong>{c.metric}</strong>
                    <span>{c.metricLabel}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="section-footer">
            <Link to="/case-studies" className="btn-outline btn-arrow">All case studies</Link>
          </div>
        </div>
      </section>

      {/* ═══ PROCESS ═══ */}
      <section className="section section-dark reveal" ref={revProcess}>
        <div className="site-container-wide">
          <span className="section-eyebrow">How we deliver</span>
          <h2 className="section-title">Six stages. Zero surprises.</h2>
          <p className="section-lead">Every engagement follows the same disciplined arc — from discovery to production handoff.</p>
          <div className="process-grid-home">
            {process.map((p, i) => (
              <div className="process-step-home" key={p.title}>
                <div className="process-number">0{i + 1}</div>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INDUSTRIES ═══ */}
      <section className="section reveal" ref={revIndustries}>
        <div className="site-container-wide">
          <div className="section-header-center">
            <span className="section-eyebrow">Industries</span>
            <h2 className="section-title">Built for the hardest verticals.</h2>
            <p className="section-lead">Regulated, real-time, data-intensive — we work where the problem is toughest.</p>
          </div>
          <div className="industries-grid-home">
            {industries.slice(0, 6).map((ind) => (
              <Link to={`/industries/${ind.id}`} className="industry-pill-home" key={ind.id}>
                <span className="industry-pill-accent" />
                <strong>{ind.name}</strong>
                <p>{ind.summary}</p>
              </Link>
            ))}
          </div>
          <div className="section-footer">
            <Link to="/industries" className="btn-outline btn-arrow">All industries</Link>
          </div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="section section-dark reveal" ref={revTestimonials}>
        <div className="site-container-wide">
          <span className="section-eyebrow">What clients say</span>
          <h2 className="section-title">Trusted by engineering leaders.</h2>
          <div className="testimonials-grid-home">
            {testimonials.slice(0, 3).map((t) => (
              <article className="testimonial-card-home professional-card" key={t.author}>
                <p>"{t.quote}"</p>
                <div className="testimonial-footer">
                  <div className="testimonial-avatar">{t.author.split(' ').map(w=>w[0]).join('')}</div>
                  <div>
                    <strong>{t.author}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRINCIPLES ═══ */}
      <section className="section reveal" ref={revPrinciples}>
        <div className="site-container-wide">
          <div className="section-header-center">
            <span className="section-eyebrow">How we operate</span>
            <h2 className="section-title">Six principles we ship by.</h2>
          </div>
          <div className="principles-grid-home">
            {principles.map((p, i) => (
              <article className="principle-home professional-card" key={p.title}>
                <span className="principle-num">0{i + 1}</span>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INSIGHTS ═══ */}
      <section className="section section-dark reveal" ref={revInsights}>
        <div className="site-container-wide">
          <div className="section-head-row">
            <div>
              <span className="section-eyebrow">Insights</span>
              <h2 className="section-title">Latest from our team.</h2>
            </div>
            <Link to="/insights" className="btn-light btn-arrow">All insights</Link>
          </div>
          <div className="insights-grid-home">
            {insights.slice(0, 3).map((ins) => (
              <Link to={`/insights/${ins.id}`} className="insight-home professional-card" key={ins.id}>
                <img src={ins.image} alt={ins.title} />
                <div className="insight-home-body">
                  <span className="eyebrow-tag">{ins.category}</span>
                  <h3>{ins.title}</h3>
                  <div className="insight-home-meta">
                    <span>{ins.date}</span>
                    <span>{ins.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CLIENTS ═══ */}
      <section className="section reveal" ref={revClients}>
        <div className="site-container-wide">
          <div className="section-header-center">
            <span className="section-eyebrow">Representative Environments</span>
            <h2 className="section-title">Representative work across demanding environments.</h2>
            <p className="section-lead">
              Anonymized examples of the industries, data shapes, and operational problems our delivery model is built for.
            </p>
          </div>
          <div className="clients-grid-home">
            {clientEnvironments.map((environment) => (
              <div className="client-cell" key={environment.name}>
                <img src={environment.icon} alt="" />
                <div>
                  <span>{environment.category}</span>
                  <strong>{environment.name}</strong>
                  <small>{environment.signal}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="final-cta reveal" ref={revCta}>
        <div className="site-container-wide">
          <div className="final-cta-card">
            <div className="final-cta-glow" />
            <span className="section-eyebrow">Ready to start?</span>
            <h2>Let's build something that ships.</h2>
            <p>Book a 30-minute consultation with a senior practitioner. No sales deck — just engineering talk.</p>
            <div className="hero-actions" style={{ justifyContent: 'center' }}>
              <Link to="/request-demo" className="btn-primary-large">Book Consultation</Link>
              <Link to="/contact" className="btn-ghost btn-arrow">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
